import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft, ArrowUp, Download, FilePen, Plus, RefreshCw, Send, Trash2, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { downloadVersion, versionLink } from '@/lib/documents';
import {
  downloadExport,
  EM_DASH,
  reportsKey,
  useReport,
  useReports,
  useReportTypes,
  type Narrative,
  type ReportDetail as Detail,
} from '@/lib/reports';
import type { Program } from '@/lib/types';
import { cn, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox, Field, Textarea, Input } from '@/components/ui/field';
import { Alert, Card, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ReportStatus } from './ReportsPage';
import { AiButton } from '@/components/ai/Assistant';

export function ReportDetail() {
  const program = useOutletContext<Program>();
  const { reportId = '' } = useParams();
  const q = useReport(program.id, reportId);

  if (q.isLoading) return <Spinner className="h-64" />;
  if (!q.data)
    return (
      <div className="mx-auto max-w-[900px] px-6 py-10">
        <Alert tone="bad" title="Report not found">
          It may be a draft you cannot see, or it was discarded. <Link to="..">Back to reports</Link>
        </Alert>
      </div>
    );
  const r = q.data;
  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      <Link to=".." relative="path" className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-text">
        <ArrowLeft className="size-4" /> Reports
      </Link>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight">
          {r.typeName}, {r.snapshot.period.label}
        </h1>
        <ReportStatus r={r} />
      </div>
      {r.correctionNote && (
        <Alert tone="info" title="Correction" className="mb-4">
          {r.correctionNote}
        </Alert>
      )}
      {r.status === 'DRAFT' ? <DraftEditor program={program} r={r} /> : <Published program={program} r={r} />}
    </div>
  );
}

/** Spreadsheet or deck downloads, rendered from the report's frozen snapshot. */
function ExportButtons({ program, r, className }: { program: Program; r: Detail; className?: string }) {
  const toast = useToast();
  const type = useReportTypes(program.id).data?.find((t) => t.key === r.type);
  return (
    <>
      {(type?.exports ?? []).map((f) => (
        <Button
          key={f}
          variant="secondary"
          className={className}
          onClick={() => downloadExport(program.id, r.id, f).catch((e) => toast.error(errorMessage(e)))}
        >
          <Download /> {f === 'xlsx' ? 'Download spreadsheet' : 'Download PowerPoint'}
        </Button>
      ))}
    </>
  );
}

/** What the draft editor asks for, by report type. */
const SUMMARY: Record<string, { label: string; hint: string }> = {
  WEEKLY_STATUS: { label: 'Executive summary', hint: 'Required. Opens the report.' },
  MONTHLY_EXECUTIVE: { label: 'Executive summary', hint: 'Required. Opens the report.' },
  RAID_LOG: { label: 'Commentary', hint: 'Optional. Printed above the log.' },
  BUDGET_TRACKING: { label: 'Commentary', hint: "Required. BlackWolf's read of the month's spend." },
  QUARTERLY_BOARD: { label: 'Overall message', hint: 'Required. Opens the first slide.' },
};
const SLIDES = ['Program on a page', 'Timeline against the plan', 'Spend against budget', 'Top risks', 'Decisions'];

// ── Published: the PDF itself, versions of the period, corrections ──

function Published({ program, r }: { program: Program; r: Detail }) {
  const manages = program.capabilities.includes('reports.manage');
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [correcting, setCorrecting] = useState(false);
  const [note, setNote] = useState('');
  const all = useReports(program.id, r.type);
  const versions = (all.data ?? []).filter((x) => x.periodStart === r.periodStart && x.status === 'PUBLISHED');
  const link = useQuery({
    queryKey: ['report-pdf', r.documentId, r.version],
    enabled: Boolean(r.documentId),
    queryFn: () => versionLink(program.id, r.documentId!, r.version, 'inline'),
    staleTime: 4 * 60_000, // links expire in five minutes
    refetchOnWindowFocus: false,
  });
  const correct = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/api/programs/${program.id}/reports/${r.id}/corrections`, { note }),
    onSuccess: (x) => {
      void qc.invalidateQueries({ queryKey: reportsKey(program.id) });
      setCorrecting(false);
      nav(`../${x.id}`, { relative: 'path' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
      <Card className="overflow-hidden p-0">
        {link.data ? (
          <iframe src={link.data} title="Published report" className="h-[80vh] w-full bg-white" />
        ) : link.isError ? (
          <p className="p-6 text-sm text-bad">{errorMessage(link.error)}</p>
        ) : (
          <Spinner className="h-[60vh]" />
        )}
      </Card>
      <div className="space-y-4">
        <Card className="space-y-3 p-4 text-sm">
          <div>
            <p className="text-xs text-muted">Published</p>
            <p>
              {fmtDateTime(r.publishedAt)}
              {r.publishedBy ? ` by ${r.publishedBy}` : ''}
            </p>
          </div>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() =>
              downloadVersion(program.id, r.documentId!, r.version).catch((e) => toast.error(errorMessage(e)))
            }
          >
            <Download /> Download PDF
          </Button>
          <ExportButtons program={program} r={r} className="w-full" />
          {manages &&
            r.current &&
            (r.draftCorrectionId ? (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => nav(`../${r.draftCorrectionId}`, { relative: 'path' })}
              >
                <FilePen /> Open correction draft
              </Button>
            ) : (
              <Button variant="secondary" className="w-full" onClick={() => setCorrecting(true)}>
                <FilePen /> Issue a correction
              </Button>
            ))}
        </Card>
        {versions.length > 1 && (
          <Card className="p-0 text-sm">
            <p className="border-b border-border px-4 py-2 font-medium">Versions of this period</p>
            <ul>
              {versions.map((v) => (
                <li key={v.id}>
                  <Link
                    to={`../${v.id}`}
                    relative="path"
                    className={cn(
                      'flex items-center justify-between px-4 py-2 hover:bg-surface-2/60',
                      v.id === r.id && 'bg-surface-2 font-medium',
                    )}
                  >
                    <span>Version {v.version}</span>
                    <span className="text-xs text-muted">{fmtDateTime(v.publishedAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
      <Dialog
        open={correcting}
        onClose={() => setCorrecting(false)}
        title="Issue a correction"
        description="This opens a new draft for the same period with the latest data and this narrative. The published version stays on record."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCorrecting(false)}>
              Cancel
            </Button>
            <Button onClick={() => correct.mutate()} loading={correct.isPending} disabled={note.trim().length < 10}>
              Open correction draft
            </Button>
          </>
        }
      >
        <Field
          label="What does the correction change?"
          hint="Printed on the corrected report. At least 10 characters."
          htmlFor="correction-note"
        >
          <Textarea
            id="correction-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
          />
        </Field>
      </Dialog>
    </div>
  );
}

// ── Draft: narrative editor beside the live preview ──

function DraftEditor({ program, r }: { program: Program; r: Detail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const monthly = r.type === 'MONTHLY_EXECUTIVE';
  const weekly = r.type === 'WEEKLY_STATUS';
  const board = r.type === 'QUARTERLY_BOARD';
  const summaryField = SUMMARY[r.type] ?? SUMMARY.WEEKLY_STATUS!;
  const requiresSummary = useReportTypes(program.id).data?.find((t) => t.key === r.type)?.requiresSummary ?? true;
  const phases = ((r.snapshot as { phases?: Array<{ number: number; name: string }> }).phases ?? []).filter(Boolean);
  const [n, setN] = useState<Required<Narrative>>({
    summary: r.narrative.summary ?? '',
    asks: r.narrative.asks ?? '',
    highlights: r.narrative.highlights ?? [],
    phaseLines: r.narrative.phaseLines ?? {},
    nextFocus: r.narrative.nextFocus ?? '',
    slideNotes: r.narrative.slideNotes ?? {},
  });
  const [dirty, setDirtyState] = useState(false);
  const dirtyRef = useRef(false);
  const setDirty = (v: boolean) => {
    dirtyRef.current = v;
    setDirtyState(v);
  };
  const [previewKey, setPreviewKey] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [notify, setNotify] = useState(true);
  const [discarding, setDiscarding] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The autosave in flight, so publish and refresh run after it rather than racing it.
  const inflight = useRef<Promise<unknown>>(Promise.resolve());
  const base = `/api/programs/${program.id}/reports/${r.id}`;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: reportsKey(program.id) });
    setPreviewKey((k) => k + 1);
  };

  const save = useMutation({
    mutationFn: (body: Narrative) => {
      const p = api.patch<{ narrative: Narrative; emDashFields: string[] }>(`${base}/narrative`, body);
      inflight.current = p.catch(() => undefined);
      return p;
    },
    onSuccess: () => {
      setDirty(false);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  /** Wait for any autosave in flight, then save what is on screen if it changed. */
  const settle = async () => {
    clearTimeout(timer.current);
    await inflight.current;
    if (dirtyRef.current) {
      await api.patch(`${base}/narrative`, latest.current);
      setDirty(false);
    }
  };
  // The latest narrative, so a debounced save always sends every field.
  const latest = useRef(n);
  const update = (patch: Partial<Narrative>) => {
    latest.current = { ...latest.current, ...patch } as Required<Narrative>;
    setN(latest.current);
    setDirty(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save.mutate(latest.current), 900);
  };
  const flush = () => {
    if (!dirty) return;
    clearTimeout(timer.current);
    save.mutate(latest.current);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const regenerate = useMutation({
    mutationFn: async () => {
      await settle();
      return api.post(`/api/programs/${program.id}/reports`, { type: r.type, periodStart: r.periodStart });
    },
    onSuccess: () => {
      setDirty(false);
      refresh();
      toast.ok('Data refreshed. Your narrative is unchanged.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const publish = useMutation({
    mutationFn: async () => {
      await settle();
      return api.post<{ notified: number }>(`${base}/publish`, { notify });
    },
    onSuccess: (x) => {
      setPublishing(false);
      refresh();
      toast.ok(
        x.notified ? `Published. ${x.notified} committee member${x.notified === 1 ? '' : 's'} notified.` : 'Published.',
      );
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const [draftProposal, setDraftProposal] = useState<{ runId: string; narrative: Narrative } | null>(null);
  const draft = useMutation({
    mutationFn: () => api.post<{ runId: string; narrative: Narrative }>(`${base}/draft-narrative`),
    onSuccess: setDraftProposal,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const applyDraft = () => {
    if (!draftProposal) return;
    const d = draftProposal.narrative;
    update({
      summary: d.summary ?? '',
      asks: d.asks ?? '',
      ...(d.highlights ? { highlights: d.highlights } : {}),
      ...(d.phaseLines ? { phaseLines: d.phaseLines } : {}),
      ...(d.nextFocus !== undefined ? { nextFocus: d.nextFocus } : {}),
    });
    void api
      .post(`/api/programs/${program.id}/ai/runs/${draftProposal.runId}/accept`, {
        recordType: 'Report',
        recordId: r.id,
      })
      .catch(() => undefined);
    setDraftProposal(null);
    toast.ok('Draft placed in the editor. Edit it before publishing.');
  };
  const discard = useMutation({
    mutationFn: () => api.delete(base),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsKey(program.id) });
      nav('..', { relative: 'path' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const dashes = [
    n.summary.includes(EM_DASH) && 'executive summary',
    n.asks.includes(EM_DASH) && 'asks',
    ...n.highlights.map((h, i) => h.includes(EM_DASH) && `highlight ${i + 1}`),
    ...Object.entries(n.phaseLines).map(([k, v]) => v.includes(EM_DASH) && `phase ${k} line`),
    n.nextFocus.includes(EM_DASH) && "next month's focus",
    ...Object.entries(n.slideNotes).map(([k, v]) => v.includes(EM_DASH) && `slide ${k} line`),
  ].filter(Boolean) as string[];
  const move = (i: number, d: -1 | 1) => {
    const h = [...n.highlights];
    [h[i], h[i + d]] = [h[i + d]!, h[i]!];
    update({ highlights: h });
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(340px,440px)_1fr]">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setPublishing(true)}
            disabled={(requiresSummary && !n.summary.trim()) || dashes.length > 0}
          >
            <Send /> Publish
          </Button>
          <Button variant="secondary" onClick={() => regenerate.mutate()} loading={regenerate.isPending}>
            <RefreshCw /> Refresh data
          </Button>
          {(weekly || monthly) && (
            <AiButton program={program} onClick={() => draft.mutate()} loading={draft.isPending}>
              Draft with the assistant
            </AiButton>
          )}
          <ExportButtons program={program} r={r} />
          <Button variant="danger" onClick={() => setDiscarding(true)}>
            <Trash2 /> Discard
          </Button>
        </div>
        <p className="text-xs text-muted">
          {save.isPending ? 'Saving...' : dirty ? 'Unsaved changes' : `Saved. Data as of ${fmtDateTime(r.updatedAt)}.`}
        </p>
        {dashes.length > 0 && (
          <Alert tone="warn" title="Em dashes are not allowed in reports">
            Rewrite: {dashes.join(', ')}.
          </Alert>
        )}
        <Field label={summaryField.label} hint={summaryField.hint} htmlFor="summary">
          <Textarea
            id="summary"
            rows={6}
            value={n.summary}
            onChange={(e) => update({ summary: e.target.value })}
            onBlur={flush}
          />
        </Field>
        {(weekly || monthly) && (
          <Field label="Asks of the Steering Committee" hint="Leave empty for no asks." htmlFor="asks">
            <Textarea
              id="asks"
              rows={3}
              value={n.asks}
              onChange={(e) => update({ asks: e.target.value })}
              onBlur={flush}
            />
          </Field>
        )}
        {board && (
          <div className="space-y-2">
            <p className="text-[13px] font-medium">A line per slide</p>
            <p className="text-xs text-muted">
              Printed under each slide title and kept as the speaker note in the PowerPoint.
            </p>
            {SLIDES.map((t, i) => (
              <Field key={t} label={`${i + 1}. ${t}`} htmlFor={`sl-${i + 1}`}>
                <Input
                  id={`sl-${i + 1}`}
                  value={n.slideNotes[String(i + 1)] ?? ''}
                  maxLength={600}
                  onChange={(e) => update({ slideNotes: { ...n.slideNotes, [String(i + 1)]: e.target.value } })}
                  onBlur={flush}
                />
              </Field>
            ))}
          </div>
        )}
        {monthly && (
          <>
            <div className="space-y-2">
              <p className="text-[13px] font-medium">A line per phase</p>
              {phases.map((ph) => (
                <Field key={ph.number} label={`${ph.number}. ${ph.name}`} htmlFor={`pl-${ph.number}`}>
                  <Input
                    id={`pl-${ph.number}`}
                    value={n.phaseLines[String(ph.number)] ?? ''}
                    maxLength={600}
                    onChange={(e) => update({ phaseLines: { ...n.phaseLines, [String(ph.number)]: e.target.value } })}
                    onBlur={flush}
                  />
                </Field>
              ))}
            </div>
            <Field label="Next month's focus" htmlFor="next-focus">
              <Textarea
                id="next-focus"
                rows={3}
                value={n.nextFocus}
                onChange={(e) => update({ nextFocus: e.target.value })}
                onBlur={flush}
              />
            </Field>
          </>
        )}
        <div className={weekly ? 'space-y-2' : 'hidden'}>
          <p className="text-[13px] font-medium">Highlights</p>
          {n.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-1">
              <Input
                value={h}
                aria-label={`Highlight ${i + 1}`}
                onChange={(e) => update({ highlights: n.highlights.map((x, j) => (j === i ? e.target.value : x)) })}
                onBlur={flush}
              />
              <Button variant="ghost" size="icon" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                <ArrowUp />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Move down"
                disabled={i === n.highlights.length - 1}
                onClick={() => move(i, 1)}
              >
                <ArrowDown />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove highlight"
                onClick={() => update({ highlights: n.highlights.filter((_, j) => j !== i) })}
              >
                <X />
              </Button>
            </div>
          ))}
          {n.highlights.length < 15 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                latest.current = { ...latest.current, highlights: [...latest.current.highlights, ''] };
                setN(latest.current);
              }}
            >
              <Plus /> Add highlight
            </Button>
          )}
        </div>
      </div>
      <Card className="overflow-hidden p-0">
        <iframe key={previewKey} src={`${base}/preview`} title="Report preview" className="h-[82vh] w-full bg-white" />
      </Card>

      <Dialog
        open={publishing}
        onClose={() => setPublishing(false)}
        title="Publish this report?"
        description="Publishing renders the PDF and files it under Published reports. It cannot be edited afterwards; changes need a correction."
        footer={
          <>
            <Button variant="secondary" onClick={() => setPublishing(false)}>
              Cancel
            </Button>
            <Button onClick={() => publish.mutate()} loading={publish.isPending}>
              <Send /> Publish
            </Button>
          </>
        }
      >
        <Checkbox
          id="notify"
          checked={notify}
          onChange={setNotify}
          label="Notify the Steering Committee"
          hint="Sends each executive a sign-in link, never the report itself. Without email set up, the links are listed in Admin under Notifications."
        />
      </Dialog>
      <Dialog
        open={!!draftProposal}
        onClose={() => setDraftProposal(null)}
        wide
        title="Assistant draft"
        description="A starting point from the report's data. Using it replaces what is in the editor; nothing is published."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDraftProposal(null)}>
              Discard
            </Button>
            <Button onClick={applyDraft}>Use this draft</Button>
          </>
        }
      >
        {draftProposal && (
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Executive summary</p>
              <p className="whitespace-pre-wrap">{draftProposal.narrative.summary}</p>
            </div>
            {draftProposal.narrative.asks && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Asks</p>
                <p className="whitespace-pre-wrap">{draftProposal.narrative.asks}</p>
              </div>
            )}
            {draftProposal.narrative.highlights && (
              <ul className="list-disc pl-5">
                {draftProposal.narrative.highlights.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            )}
            {draftProposal.narrative.phaseLines &&
              Object.entries(draftProposal.narrative.phaseLines).map(([k, v]) => (
                <p key={k}>
                  <span className="font-medium">Phase {k}:</span> {v}
                </p>
              ))}
            {draftProposal.narrative.nextFocus && <p>{draftProposal.narrative.nextFocus}</p>}
          </div>
        )}
      </Dialog>
      <Dialog
        open={discarding}
        onClose={() => setDiscarding(false)}
        title="Discard this draft?"
        description="The narrative is lost. You can generate this period again later."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDiscarding(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => discard.mutate()} loading={discard.isPending}>
              Discard draft
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
    </div>
  );
}
